import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
export const Button = ({
  children,
  onClick,
  className = '',
  variant = 'primary',
  size = 'lg',
  href
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: 'primary' | 'accent' | 'outline';
  size?: 'md' | 'lg' | 'xl';
  href?: string;
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-bold rounded-xl transition-all duration-300 transform active:scale-95 text-center';
  const variants = {
    primary: 'bg-[#89F65C] hover:bg-[#6fd943] text-black shadow-[0_0_20px_rgba(137,246,92,0.3)] hover:shadow-[0_0_30px_rgba(137,246,92,0.5)]',
    accent: 'bg-accent hover:bg-accent-hover text-white shadow-[0_0_20px_rgba(245,158,11,0.3)]',
    outline: 'bg-transparent border-2 border-primary text-primary hover:bg-primary/10'
  };
  const sizes = {
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg w-full sm:w-auto',
    xl: 'px-10 py-5 text-xl w-full'
  };
  const classes = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;
  if (href) {
    return <a href={href} className={classes} data-id="element-296">
        {children}
      </a>;
  }
  return <button onClick={onClick} className={classes} data-id="element-297">
      {children}
    </button>;
};
export const Countdown = ({
  initialHours = 23,
  initialMinutes = 27,
  initialSeconds = 35
}) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: initialHours,
    minutes: initialMinutes,
    seconds: initialSeconds
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const format = (num: number) => num.toString().padStart(2, '0');
  return <div className="flex items-center justify-center gap-2 text-3xl md:text-5xl font-black text-accent font-mono tracking-wider" data-id="element-298">
      <div className="bg-dark-card border border-dark-border px-4 py-2 rounded-lg shadow-inner" data-id="element-299">
        {format(timeLeft.hours)}
        <span className="text-sm md:text-xl text-gray-400 ml-1" data-id="element-300">hs</span>
      </div>
      <span className="text-dark-border" data-id="element-301">:</span>
      <div className="bg-dark-card border border-dark-border px-4 py-2 rounded-lg shadow-inner" data-id="element-302">
        {format(timeLeft.minutes)}
        <span className="text-sm md:text-xl text-gray-400 ml-1" data-id="element-303">min</span>
      </div>
      <span className="text-dark-border" data-id="element-304">:</span>
      <div className="bg-dark-card border border-dark-border px-4 py-2 rounded-lg shadow-inner" data-id="element-305">
        {format(timeLeft.seconds)}
        <span className="text-sm md:text-xl text-gray-400 ml-1" data-id="element-306">seg</span>
      </div>
    </div>;
};
const fakeBuyers = ['Valentina B. de Buenos Aires compró Kit Completo + Acelerador', 'Martín G. de Córdoba compró Kit Profesional', 'Lucía M. de Rosario compró Kit Completo + Acelerador', 'Diego F. de Mendoza compró Kit Profesional', 'Camila R. de La Plata compró Kit Completo + Acelerador'];
export const ToastNotification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [buyerIndex, setBuyerIndex] = useState(0);
  useEffect(() => {
    const showInterval = setInterval(() => {
      setBuyerIndex(prev => (prev + 1) % fakeBuyers.length);
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    }, 20000);
    const initialTimeout = setTimeout(() => {
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 5000);
    }, 5000);
    return () => {
      clearInterval(showInterval);
      clearTimeout(initialTimeout);
    };
  }, []);
  return <AnimatePresence data-id="element-307">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 50,
      x: -20
    }} className="fixed bottom-4 left-4 z-50 max-w-sm bg-dark-card border border-primary/30 rounded-lg shadow-2xl p-4 flex items-start gap-3" data-id="element-308">
          <div className="bg-primary/20 p-2 rounded-full mt-1" data-id="element-309">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" data-id="element-310"></div>
          </div>
          <div className="flex-1" data-id="element-311">
            <p className="text-xs text-gray-400 font-semibold mb-1" data-id="element-312">
              ✓ Compra verificada
            </p>
            <p className="text-sm text-white" data-id="element-313">{fakeBuyers[buyerIndex]}</p>
          </div>
          <button onClick={() => setIsVisible(false)} className="text-gray-500 hover:text-white" data-id="element-314">
            <XIcon size={16} data-id="element-315" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
export const SectionWrapper = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <motion.section id={id} initial={{
  opacity: 0,
  y: 40
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6
}} className={`py-16 md:py-24 px-4 max-w-6xl mx-auto ${className}`} data-id="element-316">
    {children}
  </motion.section>;